import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { monthInputForZone } from "@/lib/preferences";

const Row = ({ label, value, currency, accent }) => (
  <div className="flex justify-between items-baseline border-b border-slate-100 py-2.5">
    <span className="text-sm text-slate-600">{label}</span>
    <span className={`font-mono tabular-nums font-semibold ${
      accent === "green" ? "text-emerald-600" : accent === "red" ? "text-rose-600" : "text-slate-900"
    }`}>
      {value} {currency && <span className="text-xs text-slate-500">{currency}</span>}
    </span>
  </div>
);

const monthParams = (monthValue) => {
  const [year, month] = String(monthValue || "").split("-").map(Number);
  return Number.isInteger(month) && Number.isInteger(year) ? { month, year } : {};
};

function MarketBlock({ market, selectedMonth }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    setStats(null);
    api.get(`/inventory/${market}`, { params: monthParams(selectedMonth) }).then(({ data }) => setStats(data));
  }, [market, selectedMonth]);
  if (!stats) return <div className="text-slate-500">Cargando...</div>;
  const isDetalle = market === "detalle";
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid={`inventory-${market}`}>
      <Card className="p-6 border border-slate-200 rounded-xl bg-white shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Resumen</h3>
        <p className="text-xs text-slate-500 -mt-2 mb-3">
          {stats.period_mode === "mensual"
            ? `Inventario de apertura y actividad ${stats.period_year}-${String(stats.period_month).padStart(2, "0")}`
            : "Acumulado historico mientras el cierre mensual esta pendiente"}
        </p>
        <Row label="Total comprado" value={fmt.usdt(stats.total_bought_usdt)} currency="USDT" />
        <Row label="Total vendido" value={fmt.usdt(stats.total_sold_usdt)} currency="USDT" />
        <Row label="Saldo disponible" value={fmt.usdt(stats.balance_usdt)} currency="USDT" accent="green" />
        {isDetalle ? (
          <>
            <Row label="Total invertido" value={fmt.dop(stats.total_invested)} currency="DOP" />
            <Row label="Total recibido" value={fmt.dop(stats.total_received)} currency="DOP" />
            <Row label="Costo promedio" value={fmt.dop(stats.avg_buy_price)} currency="DOP" />
            <Row label="Precio promedio venta" value={fmt.dop(stats.avg_sell_price)} currency="DOP" />
            <Row label="Beneficio por USDT" value={fmt.dop(stats.benefit_per_usdt)} currency="DOP" />
            <Row label="Ganancia / Pérdida" value={fmt.dop(stats.profit_loss)} currency="DOP" accent={stats.profit_loss >= 0 ? "green" : "red"} />
          </>
        ) : (
          <>
            <Row label="Total pagado" value={fmt.usdt(stats.total_paid)} currency="USD" />
            <Row label="Total recibido" value={fmt.usdt(stats.total_received)} currency="USD" />
            <Row label="Costo % promedio" value={fmt.pct(stats.avg_buy_pct)} />
            <Row label="Venta % promedio" value={fmt.pct(stats.avg_sell_pct)} />
            <Row label="Beneficio promedio %" value={fmt.pct(stats.benefit_avg_pct)} accent={stats.benefit_avg_pct >= 0 ? "green" : "red"} />
            <Row label="Ganancia bruta" value={fmt.usdt(stats.gross_profit_loss)} currency="USD" accent={stats.gross_profit_loss >= 0 ? "green" : "red"} />
          </>
        )}
      </Card>

      <Card className="p-6 border border-slate-200 rounded-xl bg-white shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Desglose por moneda</h3>
        <table className="w-full text-sm">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-200">
              <th className="text-left py-2">Moneda</th>
              <th className="text-right py-2">Comprado</th>
              <th className="text-right py-2">Vendido</th>
              <th className="text-right py-2">Saldo</th>
            </tr>
          </thead>
          <tbody className="font-mono tabular-nums">
            {Object.entries(stats.breakdown || {}).length === 0 && (
              <tr><td colSpan={4} className="text-center py-6 text-slate-400">Sin datos</td></tr>
            )}
            {Object.entries(stats.breakdown || {}).map(([cur, v]) => (
              <tr key={cur} className="border-b border-slate-100">
                <td className="py-2 font-semibold text-slate-800">{cur}</td>
                <td className="text-right">{fmt.usdt(v.bought)}</td>
                <td className="text-right">{fmt.usdt(v.sold)}</td>
                <td className="text-right text-emerald-600">{fmt.usdt(v.bought - v.sold)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function Inventario() {
  const [selectedMonth, setSelectedMonth] = useState(() => monthInputForZone(new Date()));

  return (
    <div className="space-y-6" data-testid="inventario-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Inventario</h1>
          <p className="text-slate-500 mt-1">Inventario por mercado y moneda</p>
        </div>
        <div className="w-full sm:w-56">
          <Label className="text-xs uppercase font-semibold text-slate-500">Mes</Label>
          <Input
            type="month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            onInput={(event) => setSelectedMonth(event.currentTarget.value)}
            className="mt-2"
            data-testid="inventory-month"
          />
        </div>
      </div>
      <Tabs defaultValue="detalle">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="detalle" data-testid="tab-inv-detalle">Mercado Detalle</TabsTrigger>
          <TabsTrigger value="mayor" data-testid="tab-inv-mayor">Mercado Mayor</TabsTrigger>
        </TabsList>
        <TabsContent value="detalle" className="mt-6"><MarketBlock market="detalle" selectedMonth={selectedMonth} /></TabsContent>
        <TabsContent value="mayor" className="mt-6"><MarketBlock market="mayor" selectedMonth={selectedMonth} /></TabsContent>
      </Tabs>
    </div>
  );
}
